import React, { useRef, useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, Title, Tooltip, Legend } from 'chart.js';
import { Metric } from '../../../interfaces/IMetricChart';
import { BreakdownType } from '../PerformanceDashboard';
import { formatValue } from '../MetricCard';

ChartJS.register(ArcElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

interface ChartStats {
  mean: number;
  stdDev: number;
  coefficientOfVariation: number;
  maxValue: number;
  minValue: number;
}

interface DonutChartProps {
  metric: Metric;
  averageValue: string;
  loading: boolean;
  labels: string[];
  chartData: number[];
  chartStats: ChartStats;
  selectedBreakdownType:string
}

const calculateStatisticsForLabel = (label: string, data: any[]) => {
  let weightedSum = 0;
  let totalWeight = 0;
  let values: number[] = [];
  let weightedValues: number[] = [];
  let weightedReleases: { weight: number }[] = [];

  data.forEach((release) => {
    let releaseWeight = 0;
    let releaseWeightedSum = 0;

    release.work_item_types.forEach((workItem: { datasets?: any[]; count: number; type?: string; value?: number }) => {
      if (Array.isArray(workItem.datasets) && workItem.datasets.length > 0) {
        workItem.datasets.forEach((dataset: any) => {
          if (dataset.set === label) {
            const value = dataset.value;
            const weight = workItem.count;

            releaseWeight += weight;
            releaseWeightedSum += value * weight;
            values.push(value);
          }
        });
      } else if (workItem.type === label && typeof workItem.value === "number") {
        const value = workItem.value;
        const weight = workItem.count;

        releaseWeight += weight;
        releaseWeightedSum += value * weight;
        values.push(value);
      }
    });

    if (releaseWeight > 0) {
      weightedValues.push(releaseWeightedSum / releaseWeight);
      weightedReleases.push({ weight: releaseWeight });
    }

    weightedSum += releaseWeightedSum;
    totalWeight += releaseWeight;
  });

  if (weightedValues.length === 0) {
    return null;
  }

  const min = Math.min(...weightedValues);
  const max = Math.max(...weightedValues);
  const weightedMean = totalWeight > 0 ? weightedSum / totalWeight : 0;
  const weightedVariance =
    totalWeight > 0
      ? weightedValues.reduce(
          (sum, value, index) =>
            sum + weightedReleases[index].weight * Math.pow(value - weightedMean, 2),
          0
        ) / totalWeight
      : 0;

  const weightedStdDev = Math.sqrt(weightedVariance);
  const weightedCV = weightedMean !== 0 ? weightedStdDev / weightedMean : 0;
  const arithmeticMean = values.reduce((a, b) => a + b, 0) / values.length;

  return {
    weightedMean: formatValue(weightedMean),
    weightedVariance: formatValue(weightedVariance),
    weightedStdDev: formatValue(weightedStdDev),
    weightedCV: formatValue(weightedCV),
    min: formatValue(min),
    max: formatValue(max),
    arithmeticMean: formatValue(arithmeticMean),
  };
};

const DoughnutChart: React.FC<DonutChartProps> = ({ metric, averageValue, loading, labels, chartData, chartStats, selectedBreakdownType }) => {
  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  
  const total = chartData.reduce((sum, value) => sum + value, 0);
  const percentages = chartData.map(value => (total === 0 ? 0 : ((value / total) * 100).toFixed(1)));

  const data = {
    labels,
    datasets: [
      {
        label: `${metric.name} Breakdown`,
        data: chartData,
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#FF9F40',
          '#9966FF',
          '#C9CBCF',
        ],
        hoverBackgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#FF9F40',
          '#9966FF',
          '#C9CBCF',
        ],
      },
    ],
  };

  const isMouseInCenter = (chart: any, mouseX: number, mouseY: number) => {
    if (!chart) return false;
    
    const centerX = chart.width / 2;
    const centerY = chart.height / 2;
    
    const cutoutPercentage = 25;
    const radius = Math.min(chart.height, chart.width) / 2;
    const cutoutRadius = (radius * cutoutPercentage) / 100;
    
    const distanceFromCenter = Math.sqrt(Math.pow(mouseX - centerX, 2) + Math.pow(mouseY - centerY, 2));
    
    return distanceFromCenter < cutoutRadius;
  };

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!chartRef.current || !containerRef.current) return;

      const chart = chartRef.current;
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      if (isMouseInCenter(chart, mouseX, mouseY)) {
        setIsHovered(true);
      } else {
        setIsHovered(false);
      }
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [chartRef.current]);
 

  const options = {
    responsive: true,
    cutout: '60%',
    radius: '80%',
    plugins: {
      legend: {
        position: 'bottom' as const,
        onClick: () => {},
      },
      tooltip: {
        callbacks: {
          title: () => '',
          label: (context: any) => {
            const label = context.label;
            const value = formatValue(context.raw);
  
            const stats = calculateStatisticsForLabel(label, selectedBreakdownType===BreakdownType.CURRENT_RELEASE? metric.current_release || []: metric.data);
  
            if (!stats) {
              return `${label}: ${value} ${metric.units} (No Data)`;
            }
  
            if (selectedBreakdownType === BreakdownType.CURRENT_RELEASE) {
              return `${label}: ${value} ${metric.units}`;
            }

            return [
              `${label}: ${value} ${metric.units}`,
              `Minimum: ${stats.min}`,
              `Maximum: ${stats.max}`,
              `σ : ${stats.weightedStdDev}`,
              `Volatility: ${stats.weightedCV}`,
            ];
          },
        },
      },
    },
  };

  const textCenter = {
    id: 'textCenter',
    beforeDatasetsDraw(chart: { height: number; width: number; ctx: any }) {
      const { ctx } = chart;
      const centerX = chart.width / 2;
      const centerY = chart.height / 2;

      ctx.save();
      const fontSize = Math.max(20, Math.min(averageValue.length / 2, 40));
      ctx.font = `bolder ${fontSize}px Arial`;
      ctx.fillStyle = 'black';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillText(averageValue, centerX, centerY - 20);
      ctx.restore();
    },
  };

  const drawPercentageOnSlices = {
    id: 'drawPercentageOnSlices',
    afterDraw(chart: any) {
      const { ctx, chartArea } = chart;
      const meta = chart.getDatasetMeta(0);
      if (!meta || !meta.data) return;
  
      ctx.save();
      ctx.font = 'bold 12px Arial';
      ctx.fillStyle = 'black';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
  
      meta.data.forEach((arc: any, index: number) => {
        const datasetValue = chart.data.datasets[0].data[index];
        if (!datasetValue) return;
  
        const midAngle = (arc.startAngle + arc.endAngle) / 2;
        
        const offset = 30;
        const x = chartArea.width / 2 + Math.cos(midAngle) * (arc.outerRadius - offset);
        const y = chartArea.height / 2 + Math.sin(midAngle) * (arc.outerRadius - offset);
  
        ctx.fillText(`${percentages[index]}%`, x, y);
      });
  
      ctx.restore();
    },
  };

  return (
    <div ref={containerRef} style={{ textAlign: 'center', position: 'relative', width: '100%', height: '100%' }}>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <Doughnut
            ref={chartRef}
            data={data}
            options={options}
            plugins={[textCenter, drawPercentageOnSlices]}
          />
          {isHovered && (
            <div className="w-[120px] absolute bg-black bg-opacity-75 text-white p-2 rounded-md text-xs pointer-events-none z-50 shadow-md transform translate-x-[200%] -translate-y-[280%] text-left">
              <div className="px-1 py-1">
                <div >Minimum: {formatValue(chartStats.minValue)}</div>
                <div >Maximum: {formatValue(chartStats.maxValue)}</div>
                <div>Mean: {formatValue(chartStats.mean)}</div>
                <div >σ: {formatValue(chartStats.stdDev)}</div>
                <div >Volatility: {formatValue(chartStats.coefficientOfVariation)}</div>
              </div>
            </div>
          )}
          </>
      )}
    </div>
  );
};

export default DoughnutChart;